import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { ThemeProvider } from '@/components/providers/theme-provider';
import { PaymentProvider } from '@/components/subscription/StripeProvider';
import { ConditionalLayout } from '@/components/layout/ConditionalLayout';
import { PageViewTracker } from '@/components/analytics/PageViewTracker';
import { AnalyticsErrorBoundary } from '@/components/analytics/AnalyticsErrorBoundary';
import { ApiConnectionStatus } from '@/components/ui/ApiConnectionStatus';
import { AuthDebugger } from '@/components/debug/AuthDebugger';
import { GlobalErrorBoundary } from '@/components/errors/GlobalErrorBoundary';
import { NotificationProvider } from '@/components/ui/NotificationSystem';

// Import Supabase authentication system
import { SupabaseAuthProvider } from '@/contexts/SupabaseAuthContext';
import {
  AuthenticatedRoute,
  AdminRoute,
  SellerRoute,
  SuperAdminRoute,
  UnauthenticatedRoute
} from '@/components/auth/UnifiedRouteGuards';

// Enhanced Loading Component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      <div className="text-sm text-muted-foreground">Loading...</div>
    </div>
  </div>
);

// CRITICAL PERFORMANCE FIX: Lazy load ALL pages
// Only HomePage and LoginPage are loaded immediately for fastest initial load

// Core pages (immediate load for critical user flows)
import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';

// Public pages (lazy loaded)
const ModernMarketplacePage = lazy(() => import('@/pages/ModernMarketplacePage'));
const EnhancedAgentDetailsPage = lazy(() => import('@/pages/EnhancedAgentDetailsPage'));
const UnifiedPricingPage = lazy(() => import('@/pages/UnifiedPricingPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const AuthPage = lazy(() => import('@/pages/AuthPage'));
const AboutUsPage = lazy(() => import('@/pages/AboutUsPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const RefundPolicyPage = lazy(() => import('@/pages/RefundPolicyPage'));
const DocsPage = lazy(() => import('@/pages/DocsPage'));
const ApiPage = lazy(() => import('@/pages/ApiPage'));

// OAuth callbacks (lazy loaded)
const GmailCallback = lazy(() => import('@/pages/oauth2/GmailCallback'));

// Subscription pages (lazy loaded)
const SubscriptionPage = lazy(() => import('@/pages/SubscriptionPage'));
const SubscriptionSuccessPage = lazy(() => import('@/pages/SubscriptionSuccessPage'));

// Authenticated user pages (lazy loaded)
const UnifiedDashboardPage = lazy(() => import('@/pages/UnifiedDashboardPage'));
const ModernProfilePage = lazy(() => import('@/pages/ModernProfilePage'));
const AdvancedSettingsPage = lazy(() => import('@/pages/AdvancedSettingsPage'));
const UpgradePage = lazy(() => import('@/pages/UpgradePage'));
const SubscriptionManagementPage = lazy(() => import('@/pages/SubscriptionManagementPage'));
const AnalyticsDashboard = lazy(() => import('@/pages/AnalyticsDashboard'));

// Heavy workflow and agent components (lazy loaded with high priority)
const UnifiedWorkflowHub = lazy(() => import('@/pages/UnifiedWorkflowHub'));
const AdvancedAgentStudio = lazy(() => import('@/pages/AdvancedAgentStudio'));
const PublishWorkflowPage = lazy(() => import('@/pages/PublishWorkflowPage'));
const PluginManagementPage = lazy(() => import('@/pages/PluginManagementPage'));
const MCPManagementPage = lazy(() => import('@/pages/MCPManagementPage').then(module => ({ default: module.MCPManagementPage })));
const MyWorkflowExecutions = lazy(() => import('@/pages/MyWorkflowExecutions'));
const PublicWorkflowAgents = lazy(() => import('@/pages/PublicWorkflowAgents'));
const NodeConfigurationTestPage = lazy(() => import('@/pages/NodeConfigurationTestPage'));

// Admin components (lazy loaded)
const ComprehensiveAdminDashboard = lazy(() => import('@/components/admin/ComprehensiveAdminDashboard'));
const UserManagement = lazy(() => import('@/components/UserManagement'));
const SuperadminLogs = lazy(() => import('@/components/admin/SuperadminLogs'));
const AdminPricingPage = lazy(() => import('@/pages/AdminPricingPage'));
const ExecutionsListPage = lazy(() => import('@/pages/ExecutionsListPage'));
const ExecutionManagementPage = lazy(() => import('@/pages/ExecutionManagementPage').then(module => ({ default: module.ExecutionManagementPage })));

// Complex components (lazy loaded)
const AgentSubscriptionManager = lazy(() => import('@/components/agent/AgentSubscriptionManager'));
const ExecutionRecovery = lazy(() => import('@/components/workflow/ExecutionRecovery'));
const DesignSystemShowcase = lazy(() => import('@/components/design/DesignSystemShowcase'));
const PremiumFeaturesDashboard = lazy(() => import('@/components/premium').then(module => ({ default: module.PremiumFeaturesDashboard })));

const MetricsPage: React.FC = () => {
  return (
    <ConditionalLayout>
      <div className="p-6 space-y-6">
        <h1 className="text-2xl font-bold">System Metrics</h1>
        {/* Health widget */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <iframe
            title="Metrics Health"
            src="/metrics/health"
            className="w-full h-48 border rounded"
          />
          <div className="col-span-2">
            <div className="border rounded p-4 h-48 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <h3 className="text-lg font-medium mb-2">Grafana Dashboard Config</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Export dashboard configuration for monitoring setup
                </p>
                <button 
                  onClick={() => {
                    alert('Grafana dashboard export - requires proper authentication implementation');
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Export Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Prometheus raw metrics */}
        <div className="mt-4">
          <iframe title="Prometheus Metrics" src="/metrics" className="w-full h-96 border rounded" />
        </div>
      </div>
    </ConditionalLayout>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes cache (renamed from cacheTime)
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (auth/permission issues)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        return failureCount < 1; // Only retry once for 5xx errors
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Reduce refetch frequency to prevent excessive API calls
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: 'always',
      // Enable deduplication to prevent duplicate requests
      structuralSharing: true,
    },
    mutations: {
      retry: false, // Don't retry mutations by default
    },
  },
});

function App() {
  return (
    <GlobalErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <NotificationProvider>
            <SupabaseAuthProvider>
              <PaymentProvider>
                <Router
                  future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true
                  }}
                >
                <AnalyticsErrorBoundary>
                  <PageViewTracker />
                  <div className="min-h-screen bg-background">
                    <Routes>
                      {/* Root Route - Redirect authenticated users to dashboard, show homepage for guests */}
                      <Route path="/" element={
                        <UnauthenticatedRoute redirectTo="/dashboard">
                          <ConditionalLayout><HomePage /></ConditionalLayout>
                        </UnauthenticatedRoute>
                      } />
                      <Route path="/marketplace" element={
                        <Suspense fallback={<PageLoader />}>
                          <ConditionalLayout><ModernMarketplacePage /></ConditionalLayout>
                        </Suspense>
                      } />
                      <Route path="/marketplace/publish" element={
                        <SellerRoute>
                          <Suspense fallback={<PageLoader />}>
                            <ConditionalLayout><PublishWorkflowPage /></ConditionalLayout>
                          </Suspense>
                        </SellerRoute>
                      } />
                      <Route path="/agent/:id" element={
                        <Suspense fallback={<PageLoader />}>
                          <ConditionalLayout><EnhancedAgentDetailsPage /></ConditionalLayout>
                        </Suspense>
                      } />
                      <Route path="/pricing" element={
                        <Suspense fallback={<PageLoader />}>
                          <ConditionalLayout><UnifiedPricingPage /></ConditionalLayout>
                        </Suspense>
                      } />
                      <Route path="/contact" element={
                        <Suspense fallback={<PageLoader />}>
                          <ConditionalLayout><ContactPage /></ConditionalLayout>
                        </Suspense>
                      } />
                      
                      {/* Authentication Routes - Use UnauthenticatedRoute to auto-redirect logged in users */}
                      <Route path="/login" element={
                        <UnauthenticatedRoute redirectTo="/dashboard">
                          <ConditionalLayout><LoginPage /></ConditionalLayout>
                        </UnauthenticatedRoute>
                      } />
                      <Route path="/register" element={
                        <UnauthenticatedRoute redirectTo="/dashboard">
                          <Suspense fallback={<PageLoader />}>
                            <ConditionalLayout><RegisterPage /></ConditionalLayout>
                          </Suspense>
                        </UnauthenticatedRoute>
                      } />
                      <Route path="/forgot-password" element={
                        <UnauthenticatedRoute redirectTo="/dashboard">
                          <Suspense fallback={<PageLoader />}>
                            <ConditionalLayout><ForgotPasswordPage /></ConditionalLayout>
                          </Suspense>
                        </UnauthenticatedRoute>
                      } />
                      <Route path="/auth" element={
                        <UnauthenticatedRoute redirectTo="/dashboard">
                          <Suspense fallback={<PageLoader />}>
                            <ConditionalLayout><AuthPage /></ConditionalLayout>
                          </Suspense>
                        </UnauthenticatedRoute>
                      } />
                      
                      {/* Legal Pages - Public Access */}
                      <Route path="/about-us" element={
                        <Suspense fallback={<PageLoader />}>
                          <ConditionalLayout><AboutUsPage /></ConditionalLayout>
                        </Suspense>
                      } />
                      <Route path="/terms" element={
                        <Suspense fallback={<PageLoader />}>
                          <ConditionalLayout><TermsPage /></ConditionalLayout>
                        </Suspense>
                      } />
                      <Route path="/privacy" element={
                        <Suspense fallback={<PageLoader />}>
                          <ConditionalLayout><PrivacyPage /></ConditionalLayout>
                        </Suspense>
                      } />
                      <Route path="/refund-policy" element={
                        <Suspense fallback={<PageLoader />}>
                          <ConditionalLayout><RefundPolicyPage /></ConditionalLayout>
                        </Suspense>
                      } />

                      {/* Documentation and API - Public Access */}
                      <Route path="/docs" element={
                        <Suspense fallback={<PageLoader />}>
                          <ConditionalLayout><DocsPage /></ConditionalLayout>
                        </Suspense>
                      } />
                      <Route path="/api" element={
                        <Suspense fallback={<PageLoader />}>
                          <ConditionalLayout><ApiPage /></ConditionalLayout>
                        </Suspense>
                      } />

                      {/* OAuth2 Callback Routes - No authentication required */}
                      <Route path="/oauth2/gmail/callback" element={
                        <Suspense fallback={<PageLoader />}>
                          <GmailCallback />
                        </Suspense>
                      } />

                      {/* Subscription Routes */}
                      <Route path="/subscribe/:tierId" element={
                        <Suspense fallback={<PageLoader />}>
                          <ConditionalLayout><SubscriptionPage /></ConditionalLayout>
                        </Suspense>
                      } />
                      <Route path="/subscription-success" element={
                        <Suspense fallback={<PageLoader />}>
                          <ConditionalLayout><SubscriptionSuccessPage /></ConditionalLayout>
                        </Suspense>
                      } />

                      {/* ================================= */}
                      {/* UNIFIED DASHBOARD - Single dashboard for all roles */}
                      {/* ================================= */}
                      <Route path="/dashboard" element={
                        <AuthenticatedRoute>
                          <Suspense fallback={<PageLoader />}>
                            <ConditionalLayout><UnifiedDashboardPage /></ConditionalLayout>
                          </Suspense>
                        </AuthenticatedRoute>
                      } />
                      
                      {/* Subscription Management - Authenticated Users */}
                      <Route path="/subscription" element={
                        <AuthenticatedRoute>
                          <ConditionalLayout><SubscriptionManagementPage /></ConditionalLayout>
                        </AuthenticatedRoute>
                      } />
                      
                      {/* Agent Subscriptions - Persistent AI Services */}
                      <Route path="/my-ai-services" element={
                        <AuthenticatedRoute>
                          <ConditionalLayout><AgentSubscriptionManager /></ConditionalLayout>
                        </AuthenticatedRoute>
                      } />

                      {/* ================================= */}
                      {/* AUTHENTICATED USER ROUTES */}
                      {/* ================================= */}
                      <Route path="/profile" element={
                        <AuthenticatedRoute>
                          <ConditionalLayout><ModernProfilePage /></ConditionalLayout>
                        </AuthenticatedRoute>
                      } />
                      <Route path="/settings" element={
                        <AuthenticatedRoute>
                          <ConditionalLayout><AdvancedSettingsPage /></ConditionalLayout>
                        </AuthenticatedRoute>
                      } />
                      <Route path="/upgrade" element={
                        <AuthenticatedRoute>
                          <ConditionalLayout><UpgradePage /></ConditionalLayout>
                        </AuthenticatedRoute>
                      } />


                      {/* Analytics Dashboard - Permission-based access */}
                      <Route path="/analytics" element={
                        <AuthenticatedRoute>
                          <ConditionalLayout><AnalyticsDashboard /></ConditionalLayout>
                        </AuthenticatedRoute>
                      } />

                      {/* Execution Recovery */}
                      <Route path="/execution-recovery" element={
                        <AuthenticatedRoute>
                          <ConditionalLayout><ExecutionRecovery /></ConditionalLayout>
                        </AuthenticatedRoute>
                      } />

                      {/* Executions List - Super Admin Only */}
                      <Route path="/executions" element={
                        <SuperAdminRoute>
                          <ConditionalLayout><ExecutionsListPage /></ConditionalLayout>
                        </SuperAdminRoute>
                      } />

                      {/* Execution Details - Super Admin Only */}
                      <Route path="/executions/:executionId" element={
                        <SuperAdminRoute>
                          <ConditionalLayout><ExecutionManagementPage /></ConditionalLayout>
                        </SuperAdminRoute>
                      } />

                      {/* Design System Showcase */}
                      <Route path="/design-system" element={
                        <AuthenticatedRoute>
                          <ConditionalLayout><DesignSystemShowcase /></ConditionalLayout>
                        </AuthenticatedRoute>
                      } />

                      {/* ================================= */}
                      {/* SELLER ROUTES - Seller and above */}
                      {/* ================================= */}
                      <Route path="/custom-agents" element={
                        <SellerRoute>
                          <ConditionalLayout><AdvancedAgentStudio /></ConditionalLayout>
                        </SellerRoute>
                      } />
                      {/* ================================= */}
                      {/* UNIFIED WORKFLOW ROUTES - Consolidated workflow experience */}
                      {/* ================================= */}
                      {/* Main Workflow Hub - Replaces separate workflow pages */}
                      <Route path="/workflows" element={
                        <SellerRoute>
                          <Suspense fallback={<PageLoader />}>
                            <ConditionalLayout><UnifiedWorkflowHub /></ConditionalLayout>
                          </Suspense>
                        </SellerRoute>
                      } />

                      {/* Legacy Routes - Redirect to unified hub */}
                      <Route path="/agents/create" element={
                        <SellerRoute>
                          <Suspense fallback={<PageLoader />}>
                            <ConditionalLayout><UnifiedWorkflowHub /></ConditionalLayout>
                          </Suspense>
                        </SellerRoute>
                      } />
                      <Route path="/workflows/designer" element={
                        <SellerRoute>
                          <Suspense fallback={<PageLoader />}>
                            <ConditionalLayout><UnifiedWorkflowHub /></ConditionalLayout>
                          </Suspense>
                        </SellerRoute>
                      } />
                      {/* Plugin Management Route - Phase 1 Implementation */}
                      <Route path="/plugins" element={
                        <SellerRoute>
                          <ConditionalLayout><PluginManagementPage /></ConditionalLayout>
                        </SellerRoute>
                      } />
                      
                      {/* MCP Tools Management Route - Smart Tool Selection */}
                      <Route path="/mcp-tools" element={
                        <AuthenticatedRoute>
                          <ConditionalLayout><MCPManagementPage /></ConditionalLayout>
                        </AuthenticatedRoute>
                      } />

                      {/* My Workflow Executions - User Pod Management */}
                      <Route path="/my-workflows" element={
                        <AuthenticatedRoute>
                          <ConditionalLayout><MyWorkflowExecutions /></ConditionalLayout>
                        </AuthenticatedRoute>
                      } />

                      {/* Public Workflow Agents - Discovery */}
                      <Route path="/workflow-agents" element={
                        <ConditionalLayout>
                          <PublicWorkflowAgents />
                        </ConditionalLayout>
                      } />

                      {/* Premium AI Features Route - Sellers and above */}
                      <Route path="/premium" element={
                        <SellerRoute>
                          <ConditionalLayout>
                            <div className="container mx-auto px-4 py-8 max-w-7xl">
                              <PremiumFeaturesDashboard />
                            </div>
                          </ConditionalLayout>
                        </SellerRoute>
                      } />

                      {/* Node Configuration Test Page - Development/Testing */}
                      <Route path="/node-config-test" element={
                        <AuthenticatedRoute>
                          <ConditionalLayout>
                            <NodeConfigurationTestPage />
                          </ConditionalLayout>
                        </AuthenticatedRoute>
                      } />

                      {/* ================================= */}
                      {/* ADMIN ROUTES - Admin and Super Admin */}
                      {/* ================================= */}
                      <Route path="/admin" element={
                        <AdminRoute>
                          <ConditionalLayout><ComprehensiveAdminDashboard /></ConditionalLayout>
                        </AdminRoute>
                      } />
                      <Route path="/admin/users" element={
                        <AdminRoute>
                          <ConditionalLayout><UserManagement /></ConditionalLayout>
                        </AdminRoute>
                      } />
                      <Route path="/admin/execution-logs" element={
                        <AdminRoute>
                          <ConditionalLayout>
                            <SuperadminLogs />
                          </ConditionalLayout>
                        </AdminRoute>
                      } />
                      <Route path="/admin/metrics" element={
                        <AdminRoute>
                          <ConditionalLayout>
                            <MetricsPage />
                          </ConditionalLayout>
                        </AdminRoute>
                      } />


                      {/* ================================= */}
                      {/* SUPER ADMIN ROUTES */}
                      {/* ================================= */}
                      <Route path="/admin/pricing" element={
                        <SuperAdminRoute>
                          <ConditionalLayout><AdminPricingPage /></ConditionalLayout>
                        </SuperAdminRoute>
                      } />
                      <Route path="/admin/system" element={
                        <SuperAdminRoute>
                          <ConditionalLayout>
                            <div className="p-8 text-center">
                              <h1 className="text-2xl font-bold mb-4">System Management</h1>
                              <p className="text-gray-600">Advanced system management features coming soon.</p>
                            </div>
                          </ConditionalLayout>
                        </SuperAdminRoute>
                      } />

                      {/* ================================= */}
                      {/* LEGACY ROUTE REDIRECTS - For compatibility */}
                      {/* ================================= */}
                      <Route path="/admin-dashboard" element={
                        <AdminRoute>
                          <ConditionalLayout><UnifiedDashboardPage /></ConditionalLayout>
                        </AdminRoute>
                      } />
                      <Route path="/seller/*" element={
                        <SellerRoute>
                          <ConditionalLayout><UnifiedDashboardPage /></ConditionalLayout>
                        </SellerRoute>
                      } />
                      <Route path="/admin/*" element={
                        <AdminRoute>
                          <ConditionalLayout><UnifiedDashboardPage /></ConditionalLayout>
                        </AdminRoute>
                      } />

                      {/* ================================= */}
                      {/* 404 CATCH-ALL ROUTE */}
                      {/* ================================= */}
                      <Route path="*" element={
                        <ConditionalLayout>
                          <div className="min-h-screen flex items-center justify-center bg-gray-50">
                            <div className="text-center">
                              <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                              <p className="text-gray-600 mb-8">Page not found</p>
                              <a 
                                href="/dashboard" 
                                className="inline-block bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors"
                              >
                                Go to Dashboard
                              </a>
                            </div>
                          </div>
                        </ConditionalLayout>
                      } />
                    </Routes>
                    
                    {/* Global UI Components */}
                    <ApiConnectionStatus />
                    <AuthDebugger />
                    <Toaster />

                    {/* Platform access removed - using only LangGraph workflow builder */}
                  </div>
                </AnalyticsErrorBoundary>
                </Router>
              </PaymentProvider>
            </SupabaseAuthProvider>
          </NotificationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </GlobalErrorBoundary>
  );
}

export default App;
